import { Component, OnInit, ViewChild } from '@angular/core';
import { NzDrawerRef, NzMessageService } from 'ng-zorro-antd';
import { Employee } from '@app/_models/_DTOs/employee';
import { Department } from '@app/_models/_DTOs/department';
import { Supplier } from '@app/_models/_DTOs/supplier';
import { AssetHistoryActionType } from '@app/_models/assetHistoryActionType';
import { AssetType } from '@app/_models/enums/assetType';
import { EmployeeService } from '@app/_services/employee.service';
import { AssetService, AuthenticationService } from '@app/_services';
import { LocationService } from '@app/_services/location.service';
import { DepartmentService } from '@app/_services/department.service';
import { SupplierService } from '@app/_services/supplier.service';
import { environment } from '@environments/environment';
import { NgForm } from '@angular/forms';
import { Asset, AssetTypeName } from '@app/_models/_DTOs/asset';
import { Location } from '@app/_models/_DTOs/location';
import { ITuple } from '@app/_models/tuple';
import { AssetHistoryActionName } from '@app/_models/_DTOs/assetHistory';
import { Subject } from 'rxjs';
import { EmployeeViewModel } from '@app/_models/_viewModels/employee.viewmodel';
import { Sort } from '@app/_models/sort';
import { SortDirection } from '@app/_models/enums/sortDirection';
import { BaseComponent } from '@app/_shared/base.component';
import { SCREEN_SIZE } from '@app/_helpers/screen-size.enum';
import { UserToken } from '@app/_models';
import { ResizeService } from '@app/_services/resize.service';
import { LegalEntity } from '@app/_models/_DTOs/legalEntity';
import { LegalEntityService } from '@app/_services/legalEntity.service';

@Component({
  selector: "app-employees",
  templateUrl: "./employees.component.html",
  styleUrls: ["./employees.component.css"],
})
export class EmployeesComponent extends BaseComponent implements OnInit {
  onScreenResize(screenSize: SCREEN_SIZE) {}
  onUserAvailable(userToken: UserToken) {}
  level: number = 0;

  sorts: Sort[] = [];
  pageIndex = 1;
  pageSize = 10;
  total = 1;
  employeeList: Employee[] = [];
  loading = true;

  loadingDropdowns = false;
  detailsDrawerVisible: boolean = false;
  editDrawerVisible: boolean = false;
  isSavingEmployee: boolean = false;
  isEditing: boolean = false;

  locations: Location[] = [];
  departments: Department[] = [];
  employees: Employee[] = [];
  legalEntities: LegalEntity[] = [];

  selectedEmployee: Employee;
  employeeViewModel: EmployeeViewModel = null;

  employeeIdSearchValue: number[] = [];
  locationIdSearchValue: number[] = [];
  departmentIdSearchValue: number[] = [];
  legalEntityIdSearchValue: number[] = [];
  pinSearchValue = "";

  public assetHistoryActionType = AssetHistoryActionType;
  public assetTypes = AssetType;

  currencyFormatter = (value: number) => `${value} EUR`;
  currencyParser = (value: string) => value.replace(" EUR", "");

  errorMessages = {};

  constructor(
    private employeeService: EmployeeService,
    private assetService: AssetService,
    private locationService: LocationService,
    private departmentService: DepartmentService,
    private messageService: NzMessageService,
    private legalEntityService: LegalEntityService,
    protected authenticationService: AuthenticationService,
    protected resizeService: ResizeService
  ) {
    super(authenticationService, resizeService);
    this.initModel();
  }

  ngOnInit() {
    this.searchData();
    this.loadDataForDropdowns();
  }

  initModel(employee?: Employee) {
    if (employee) {
      this.employeeViewModel = {
        id: employee.id ? employee.id : -1,
        firstName: employee.firstName ? employee.firstName : "",
        lastName: employee.lastName ? employee.lastName : "",
        pin: employee.pin ? employee.pin : undefined,
        departmentId: employee.department ? employee.department.id : undefined,
        locationId: employee.location ? employee.location.id : undefined,
      };
    } else {
      this.employeeViewModel = {
        id: -1,
        firstName: "",
        lastName: "",
        pin: undefined,
        departmentId: undefined,
        locationId: undefined,
      };
    }
  }

  saveEmployee(form: NgForm) {
    this.isSavingEmployee = true;
    if (this.isEditing) {
      this.employeeService
        .put(this.employeeViewModel, this.employeeViewModel.id)
        .subscribe(
          (result) => {
            this.messageService.create(
              "success",
              "Zaposlenik uspješno izmijenjen",
              {
                nzDuration: 3000,
              }
            );
            this.isSavingEmployee = false;
            this.searchData();
            form.reset();
            this.closeEditDrawer();
          },
          (error) => {
            if (error.ParamName) {
              if (form.controls[error.ParamName]) {
                form.controls[error.ParamName].setErrors({ custom: true });
                var errorMessage = error.Message.split("Parameter name")[0];
                this.errorMessages[error.ParamName] = errorMessage;
              }
            }
            this.isSavingEmployee = false;
          }
        );
    } else {
      this.employeeService
        .post<EmployeeViewModel>(this.employeeViewModel)
        .subscribe(
          (result) => {
            this.messageService.create("success", "Zaposlenik uspješno dodan");
            this.isSavingEmployee = false;
            this.searchData();
            form.reset();
            this.closeEditDrawer();
          },
          (error) => {
            if (error.ParamName) {
              if (form.controls[error.ParamName]) {
                form.controls[error.ParamName].setErrors({ custom: true });
                var errorMessage = error.Message.split("Parameter name")[0];
                this.errorMessages[error.ParamName] = errorMessage;
              }
            }
            this.isSavingEmployee = false;
          }
        );
    }
  }

  editEmployee(employee: Employee) {
    this.initModel(employee);
    this.isEditing = true;
    this.openEditDrawer();
  }

  deleteEmployee(id: number) {
    this.employeeService.delete(id).subscribe(
      (result) => {
        this.messageService.create("success", "Zaposlenik uspješno obrisan");
        this.searchData();
      },
      (error) => {
        console.log(error);
      }
    );
  }

  loadDataForDropdowns() {
    this.loadingDropdowns = true;
    this.locationService.getAllBasic().subscribe((data: Location[]) => {
      this.locations = data;
    });
    this.departmentService.getAllBasic().subscribe((data: Department[]) => {
      this.departments = data;
    });
    this.employeeService.getAllBasic().subscribe((data: Employee[]) => {
      this.employees = data;
    });
    this.legalEntityService.getAllBasic().subscribe((data: LegalEntity[]) => {
      this.legalEntities = data;
    });
  }

  sort(sort: { key: string; value: string }): void {
    var newSort: Sort = {
      PropertyName: sort.key,
      Direction: null,
    };
    if (sort.value) {
      if (sort.value == "ascend") {
        newSort.Direction = SortDirection.Ascending;
      } else {
        newSort.Direction = SortDirection.Descending;
      }
    }

    if (newSort.Direction == null) {
      var existingSort = this.sorts.find(
        (x) => x.PropertyName == newSort.PropertyName
      );
      if (existingSort) {
        const index = this.sorts.indexOf(existingSort);
        if (index > -1) {
          this.sorts.splice(index, 1);
        }
      }
    } else {
      var existingSort = this.sorts.find(
        (x) => x.PropertyName == newSort.PropertyName
      );
      if (existingSort) {
        existingSort.Direction = newSort.Direction;
      } else {
        this.sorts.push(newSort);
      }
    }
    this.searchData();
  }

  getSortString() {
    var sortString = "";
    if (this.sorts && this.sorts.length > 0) {
      for (let sort of this.sorts) {
        if (sort.Direction == SortDirection.Ascending) {
          if (sortString.length == 0) {
            sortString = sort.PropertyName;
          } else {
            sortString += "," + sort.PropertyName;
          }
        } else {
          if (sortString.length == 0) {
            sortString = "-" + sort.PropertyName;
          } else {
            sortString += ",-" + sort.PropertyName;
          }
        }
      }
    }
    return sortString;
  }

  searchData(reset: boolean = false): void {
    if (reset) {
      this.pageIndex = 1;
    }

    var filters = "";
    if (this.employeeIdSearchValue.length > 0) {
      filters += "Id==" + this.employeeIdSearchValue.join("|") + ",";
    }
    if (this.pinSearchValue.length > 0) {
      filters += "EmployeeOib@=" + this.pinSearchValue + ",";
    }
    if (this.departmentIdSearchValue.length > 0) {
      filters +=
        "DepartmentId==" + this.departmentIdSearchValue.join("|") + ",";
    }
    if (this.legalEntityIdSearchValue.length > 0) {
      filters +=
        "Department.LegalEntityId==" + this.legalEntityIdSearchValue.join("|") + ",";
    }
    if (this.locationIdSearchValue.length > 0) {
      filters += "LocationId==" + this.locationIdSearchValue.join("|") + ",";
    }

    var sortString = this.getSortString();
    if (sortString.length == 0) {
      sortString = "-Created";
    } else {
      sortString += ",-Created";
    }

    this.loading = true;
    this.employeeService
      .getPage(this.pageIndex, this.pageSize, sortString, filters)
      .subscribe((data: ITuple<Employee>) => {
        this.loading = false;
        this.total = data.item2;
        this.employeeList = data.item1;
      });
  }

  reset(): void {
    this.employeeIdSearchValue = [];
    this.departmentIdSearchValue = [];
    this.locationIdSearchValue = [];
    this.legalEntityIdSearchValue = [];
    this.pinSearchValue = "";
    this.searchData(true);
  }

  // onLocationInput(value: string): void {
  //   this.filteredLocations = this.locations.filter(location => location.name.toLowerCase().includes(value.toLowerCase()));
  // }

  openEditDrawer() {
    this.editDrawerVisible = true;
  }

  closeEditDrawer(form?: NgForm) {
    this.isEditing = false;
    this.editDrawerVisible = false;
    if (form) {
      form.reset();
    }
    this.initModel();
  }

  openDetails(employee: Employee) {
    this.employeeService.get(employee.id).subscribe((data: Employee) => {
      this.selectedEmployee = data;
    });
  }

  closeDetails() {
    this.selectedEmployee = null;
  }
}
